/**
 * Pure mapping between ant's chat model and the shapes the support pages
 * render. No I/O here, so it is unit-tested on its own.
 *
 * The console's "Conversation" is an ant ChatSession (the unit CS answers and
 * closes); its "Message" is an ant ChatMessage keyed by that session id so the
 * pages keep working. ant's conversation id travels along as `conversation_id`
 * because chatMessages and markChatReadByAdmin take that one.
 */

import {
  AntAdminUser,
  AntChatMessage,
  AntChatSession,
  AntUser,
  RiderIdentity,
} from './api';
import { Conversation, Message, MessageAction, MessageMedia } from './types';

const lower = (value: string | null | undefined): string => (value ?? '').toLowerCase();

export function fullName(
  person: Pick<AntUser, 'firstName' | 'lastName'> | AntAdminUser | null | undefined
): string {
  if (!person) return '';
  return `${person.firstName ?? ''} ${person.lastName ?? ''}`.trim();
}

function adminName(admin: AntAdminUser | null | undefined): string | null {
  if (!admin) return null;
  return fullName(admin) || admin.email || null;
}

/** Millisecond epoch of an ISO timestamp, or NaN. */
const ms = (iso: string | null | undefined): number => (iso ? Date.parse(iso) : NaN);

/** `a` strictly after `b`; a missing `b` counts as "never", a missing `a` as "never". */
export function isAfter(
  a: string | null | undefined,
  b: string | null | undefined
): boolean {
  const left = ms(a);
  if (Number.isNaN(left)) return false;
  const right = ms(b);
  return Number.isNaN(right) || left > right;
}

/** The later of two ISO timestamps, or null when neither parses. */
export function laterOf(
  a: string | null | undefined,
  b: string | null | undefined
): string | null {
  if (isAfter(a, b)) return a ?? null;
  if (isAfter(b, a)) return b ?? null;
  return a ?? b ?? null;
}

export function mapSenderType(
  senderType: string | null | undefined,
  visibility: string | null | undefined
): Message['sender_type'] {
  switch (lower(senderType)) {
    case 'user':
      return 'user';
    case 'admin':
      return lower(visibility) === 'internal' ? 'note' : 'admin';
    case 'assistant':
      return 'assistant';
    case 'system':
      return 'system';
    default:
      return 'system';
  }
}

export function mapPriority(priority: string | null | undefined): 'normal' | 'urgent' {
  return lower(priority) === 'urgent' ? 'urgent' : 'normal';
}

/**
 * The session with the rider's name and phone, when they have been read
 * (useChat reads them apart from the queue). Same object when nothing changes.
 */
export function withRiderIdentity(
  conversation: Conversation,
  identity: RiderIdentity | null | undefined
): Conversation {
  if (!identity) return conversation;
  const name = fullName(identity) || conversation.user_name;
  const phone = identity.phone || conversation.user_phone;
  if (
    name === conversation.user_name &&
    phone === conversation.user_phone &&
    conversation.rider_read
  ) {
    return conversation;
  }
  return { ...conversation, user_name: name, user_phone: phone, rider_read: true };
}

/** How the console names a rider: name, else phone, else "Rider" and a short id. */
export function riderLabel(
  conversation: Pick<
    Conversation,
    'id' | 'user_name' | 'user_phone' | 'user_id' | 'conversation_id'
  >
): string {
  if (conversation.user_name) return conversation.user_name;
  if (conversation.user_phone) return conversation.user_phone;
  const id = conversation.user_id || conversation.conversation_id || conversation.id;
  return `Rider ${id.slice(0, 8)}`;
}

/**
 * How the console names where a session comes from: the system and its
 * organization ("Ann Arbor · Movatic"), the system alone when ant names no
 * organization, `unnamed` for a system the token cannot name, and "General"
 * for a session with no system (Movatic CS owns those).
 */
export function systemLabel(
  conversation: Pick<Conversation, 'system_id' | 'system_name' | 'organization_name'>,
  unnamed = 'System'
): string {
  const system = conversation.system_name || (conversation.system_id ? unnamed : null);
  if (!system) return 'General';
  return conversation.organization_name
    ? `${system} · ${conversation.organization_name}`
    : system;
}

/** The queue row's preview is cut here, with an ellipsis. */
export const PREVIEW_MAX_CHARS = 100;

/**
 * What a queue row shows of a message: its text with whitespace collapsed,
 * cut at PREVIEW_MAX_CHARS; "Photo" or "Video" when it carried only media;
 * null when there is nothing to show.
 */
export function messagePreview(
  body: string | null | undefined,
  media: 'photo' | 'video' | null
): string | null {
  const text = (body ?? '').replace(/\s+/g, ' ').trim();
  if (text) {
    return text.length > PREVIEW_MAX_CHARS
      ? `${text.slice(0, PREVIEW_MAX_CHARS - 1).trimEnd()}…`
      : text;
  }
  if (media === 'photo') return 'Photo';
  if (media === 'video') return 'Video';
  return null;
}

/** The media kind of a session's newest message: bucket media, or a legacy photo. */
function sessionMessageMedia(
  message: NonNullable<AntChatSession['messages']>[number]
): 'photo' | 'video' | null {
  const kind = lower(message.media?.kind);
  if (kind === 'photo' || kind === 'video') return kind;
  return message.attachmentImageId || message.attachmentCategory ? 'photo' : null;
}

export function mapSession(session: AntChatSession): Conversation {
  const last = session.messages?.[0] ?? null;

  return {
    id: session.id,
    // Read apart from the queue: see fetchRiderIdentities.
    user_phone: '',
    user_name: '',
    user_id: session.conversation?.userId ?? null,
    system_id: session.systemId ?? null,
    system_name: session.system?.name ?? null,
    system_old_id: session.system?.oldId ?? null,
    organization_id: session.system?.organization?.id ?? null,
    organization_name: session.system?.organization?.name ?? null,
    assigned_admin_id: session.assignedAdminId ?? null,
    assigned_admin_name: adminName(session.assignedAdmin),
    status: lower(session.status) || 'open',
    context_rental_id: session.rentalId ?? null,
    context_hardware_id: null,
    user_lat: null,
    user_lng: null,
    tags: session.tags ?? [],
    priority: mapPriority(session.priority),
    resolution_note: session.resolutionNote ?? null,
    closed_at: session.closedAt ?? null,
    closed_by_admin_name: adminName(session.closedByAdmin),
    handled_by: lower(session.handledBy) === 'assistant' ? 'assistant' : 'human',
    escalated_at: session.escalatedAt ?? null,
    escalation_reason: session.escalationReason ?? null,
    csat_score: typeof session.csatScore === 'number' ? session.csatScore : null,
    csat_comment: session.csatComment ?? null,
    csat_at: session.csatAt ?? null,
    waiting_since: session.waitingSince ?? null,
    updated_on: session.lastMessageAt ?? session.updatedAt ?? session.insertedAt,
    created_on: session.insertedAt,
    conversation_id: session.conversationId,
    last_read_at_admin: session.conversation?.lastReadAtAdmin ?? null,
    last_read_at_user: session.conversation?.lastReadAtUser ?? null,
    last_message: last
      ? {
          sender_type: mapSenderType(last.senderType, last.visibility),
          created_on: last.insertedAt,
          preview: messagePreview(last.body, sessionMessageMedia(last)),
          sender_name:
            lower(last.senderType) === 'user' ? null : last.senderName?.trim() || null,
        }
      : null,
  };
}

export interface MessageContext {
  /** The POC conversation id (ant session id) the message is listed under. */
  sessionId: string;
  /** The rider's display name, for USER rows. */
  userName?: string;
}

export function mapMessage(message: AntChatMessage, context: MessageContext): Message {
  const senderType = mapSenderType(message.senderType, message.visibility);
  const imageUrl = message.attachmentImage?.url ?? null;

  // Agents and the assistant go by the name ant shows the rider ("Ana R.",
  // "Movatic Assistant"), so the console reads as the rider's screen does.
  const shownName = message.senderName?.trim() || null;
  let senderName: string;
  switch (lower(message.senderType)) {
    case 'user':
      senderName = context.userName || 'Rider';
      break;
    case 'admin':
      senderName = shownName || adminName(message.senderAdmin) || 'Support';
      break;
    case 'assistant':
      senderName = shownName || 'Assistant';
      break;
    default:
      senderName = 'System';
  }

  const pending = !imageUrl && Boolean(message.attachmentImageId);
  const media = mapMedia(message.media);
  const actions = mapActions(message.actions);
  const clientContext =
    senderType === 'user' ? parseClientContext(message.clientContext) : null;

  return {
    id: message.id,
    conversation_id: context.sessionId,
    sender_type: senderType,
    sender_id: message.senderUserId ?? message.senderAdminId ?? '',
    sender_name: senderName,
    content: message.body ?? '',
    content_type:
      media?.kind === 'video' ? 'video' : imageUrl || media ? 'image' : 'text',
    image_url: imageUrl,
    image_pending: pending,
    created_on: message.insertedAt,
    ...(media ? { media } : {}),
    ...(actions.length > 0 ? { actions } : {}),
    ...(clientContext ? { client_context: clientContext } : {}),
  };
}

/**
 * A message's photo or video from the bucket, or null: none, or a kind this
 * console does not know how to show.
 */
export function mapMedia(media: AntChatMessage['media']): MessageMedia | null {
  if (!media) return null;
  const kind = lower(media.kind);
  if (kind !== 'photo' && kind !== 'video') return null;
  return {
    kind,
    url: media.url ?? null,
    poster_url: media.posterUrl ?? null,
    content_type: media.contentType,
    byte_size: media.byteSize,
    duration_ms: media.durationMs ?? null,
    width: media.width ?? null,
    height: media.height ?? null,
  };
}

/** ant's actions on a message, lowercase kinds; empty when there are none. */
export function mapActions(actions: AntChatMessage['actions']): MessageAction[] {
  return (actions ?? [])
    .filter((a) => a && a.label)
    .map((a) => ({
      kind: lower(a.kind),
      label: a.label,
      value: a.value ?? null,
      rental_id: a.rentalId ?? null,
    }));
}

/**
 * The client context as an object: ant sends AshGraphql's Json as an object
 * to admins, but a JSON string is accepted too. Anything else, an empty
 * object included, is null.
 */
export function parseClientContext(
  value: AntChatMessage['clientContext']
): Record<string, unknown> | null {
  let parsed: unknown = value;
  if (typeof value === 'string') {
    try {
      parsed = JSON.parse(value);
    } catch (_) {
      return null;
    }
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;
  return Object.keys(parsed).length > 0 ? (parsed as Record<string, unknown>) : null;
}

/** The newest rider message that carried a client context, with it. */
export function latestClientContext(
  messages: Message[] | undefined
): { context: Record<string, unknown>; at: string } | null {
  if (!messages) return null;
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const m = messages[i];
    if (m.sender_type === 'user' && m.client_context) {
      return { context: m.client_context, at: m.created_on };
    }
  }
  return null;
}

/**
 * Rider messages Customer Support has not read. With the session's messages
 * loaded it is exact; before that, the newest message tells whether there is
 * at least one.
 */
export function unreadCount(
  conversation: Conversation,
  loadedMessages: Message[] | undefined,
  localReadAt: string | null | undefined
): number {
  const readAt = laterOf(conversation.last_read_at_admin, localReadAt);
  if (loadedMessages) {
    return loadedMessages.filter(
      (m) => m.sender_type === 'user' && isAfter(m.created_on, readAt)
    ).length;
  }
  const last = conversation.last_message;
  return last && last.sender_type === 'user' && isAfter(last.created_on, readAt) ? 1 : 0;
}

/** Where the rider last wrote from, taken from their newest located message. */
export function latestUserLocation(
  messages: AntChatMessage[]
): { lat: number; lng: number } | null {
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const m = messages[i];
    if (
      lower(m.senderType) === 'user' &&
      typeof m.sentLat === 'number' &&
      typeof m.sentLng === 'number'
    ) {
      return { lat: m.sentLat, lng: m.sentLng };
    }
  }
  return null;
}

/**
 * The rental the rider most recently wrote about, if any message names one.
 * A session opened as general keeps rental_id null even after the rider
 * writes from a rental screen, so the panel falls back to the messages.
 */
export function latestRentalId(messages: AntChatMessage[]): string | null {
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    if (messages[i].rentalId) return messages[i].rentalId;
  }
  return null;
}

/** The hardware the rider most recently wrote about, if any message names one. */
export function latestContextHardwareId(messages: AntChatMessage[]): string | null {
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    if (messages[i].contextHardwareId) return messages[i].contextHardwareId;
  }
  return null;
}

/**
 * True when two lists hold the same rows with the same values. The poll
 * rebuilds every row from scratch each tick; handing React the previous
 * reference when nothing changed keeps the thread and the queue from
 * repainting every 3 seconds.
 */
export function sameList<T>(a: T[] | undefined, b: T[]): boolean {
  if (!a || a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    if (JSON.stringify(a[i]) !== JSON.stringify(b[i])) return false;
  }
  return true;
}
